
import { useCallback, useEffect, useState } from "react";
import { useInstitute } from "./context/InstituteContext";
import {
  getInstituteStudents,
  getAllPurchases,
  getAllScores,
} from "./api/instituteclient";

const firstDefined = (...values) =>
  values.find(
    (value) => value !== undefined && value !== null && value !== ""
  );

const getArray = (response) => {
  const data = response?.data ?? response;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;

  return [];
};

const normalizeStudent = (student = {}) => ({
  ...student,
  id: firstDefined(student.id, student.profile_id),
  userId: firstDefined(student.userId, student.user_id, student.userID),
  fullName: firstDefined(
    student.fullName,
    student.full_name,
    student.student_name,
    student.name,
    student.username,
    ""
  ),
  email: firstDefined(
    student.email,
    student.student_email,
    ""
  ),
  mobile: firstDefined(student.mobile, student.phone, ""),
  registeredAt: firstDefined(
    student.registeredAt,
    student.registered_at,
    student.created_at,
    null
  ),
  status: firstDefined(
    student.status,
    student.is_active === false ? "Inactive" : "Active"
  ),
});

const normalizeRegistration = (registration = {}) => {
  const studentId = firstDefined(
    registration.userId,
    registration.user_id,
    registration.student_id
  );

  const examName = firstDefined(
    registration.examName,
    registration.exam_name,
    registration.courseName,
    registration.course_name,
    ""
  );

  return {
    ...registration,
    id: registration.id,
    userId: studentId,
    studentId,
    studentName: firstDefined(
      registration.studentName,
      registration.student_name,
      registration.fullName,
      registration.full_name,
      ""
    ),
    email: firstDefined(
      registration.email,
      registration.studentEmail,
      registration.student_email,
      ""
    ),
    examId: firstDefined(
      registration.examId,
      registration.exam_id
    ),
    examName,
    courseName: firstDefined(
      registration.courseName,
      registration.course_name,
      registration.examName,
      registration.exam_name,
      ""
    ),
    registeredAt: firstDefined(
      registration.registeredAt,
      registration.registered_at,
      null
    ),
    scheduleId: firstDefined(
      registration.scheduleId,
      registration.schedule_id
    ),
    status: firstDefined(registration.status, "REGISTERED"),
  };
};

const normalizeResult = (result = {}) => {
  const studentId = firstDefined(
    result.userId,
    result.user_id,
    result.student_id
  );

  const examName = firstDefined(
    result.courseName,
    result.course_name,
    result.examName,
    result.exam_name,
    ""
  );

  const explicitTotal = firstDefined(
    result.total,
    result.total_marks,
    result.max_marks,
    result.max_score,
    result.maximum_score
  );

  return {
    ...result,
    id: result.id,
    userId: studentId,
    studentId,
    studentName: firstDefined(
      result.studentName,
      result.student_name,
      result.fullName,
      result.full_name,
      ""
    ),
    email: firstDefined(
      result.email,
      result.studentEmail,
      result.student_email,
      ""
    ),
    registrationId: firstDefined(
      result.registrationId,
      result.registration_id
    ),
    examId: firstDefined(
      result.examId,
      result.exam_id
    ),
    examName,
    courseName: examName,
    attemptId: firstDefined(
      result.attemptId,
      result.attempt_id
    ),
    attemptStatus: firstDefined(
      result.attemptStatus,
      result.attempt_status
    ),
    totalQuestions: firstDefined(
      result.totalQuestions,
      result.total_questions
    ),
    correctAnswers: firstDefined(
      result.correctAnswers,
      result.correct_answers
    ),
    wrongAnswers: firstDefined(
      result.wrongAnswers,
      result.wrong_answers
    ),
    unansweredQuestions: firstDefined(
      result.unansweredQuestions,
      result.unanswered_questions
    ),
    score: firstDefined(result.score, 0),
    total: explicitTotal,
    result: firstDefined(
      result.result,
      result.result_status,
      result.status,
      ""
    ),
    publishedAt: firstDefined(
      result.publishedAt,
      result.published_at,
      null
    ),
    examDate: firstDefined(
      result.examDate,
      result.exam_date,
      result.publishedAt,
      result.published_at,
      null
    ),
  };
};

export const percentOf = (result) => {
  const score = Number(result?.score);
  const total = Number(result?.total);

  if (
    !Number.isFinite(score) ||
    !Number.isFinite(total) ||
    total <= 0
  ) {
    return 0;
  }

  return Math.round((score / total) * 100);
};

export const isPass = (result) => {
  const status = String(
    typeof result === "object"
      ? result?.result ??
          result?.result_status ??
          result?.status ??
          ""
      : result ?? ""
  )
    .trim()
    .toUpperCase();

  return (
    status === "PASS" ||
    status.startsWith("PASS_") ||
    status.startsWith("PASS ")
  );
};

export function fmtDate(iso) {
  if (!iso) return "—";

  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function downloadCsv(filename, rows) {
  if (!Array.isArray(rows) || rows.length === 0) return;

  const escapeCsv = (value) => {
    const text = String(value ?? "");
    return `"${text.replace(/"/g, '""')}"`;
  };

  const csv = rows
    .map((row) => row.map(escapeCsv).join(","))
    .join("\r\n");

  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
}

export default function useInstituteData() {
  const { currentInstitute } = useInstitute();

  const [data, setData] = useState({
    students: [],
    purchases: [],
    scores: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!currentInstitute) {
      setData({
        students: [],
        purchases: [],
        scores: [],
      });

      setError("");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const [
        studentsResponse,
        purchasesResponse,
        scoresResponse,
      ] = await Promise.all([
        getInstituteStudents(),
        getAllPurchases(),
        getAllScores(),
      ]);

      setData({
        students: getArray(studentsResponse).map(normalizeStudent),
        purchases: getArray(purchasesResponse).map(normalizeRegistration),
        scores: getArray(scoresResponse).map(normalizeResult),
      });
    } catch (err) {
      const responseMessage =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message;

      setError(
        responseMessage ||
          "Could not load institute data. Check your login session and Django API."
      );
    } finally {
      setLoading(false);
    }
  }, [currentInstitute]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    ...data,
    loading,
    error,
    setError,
    reload: load,
  };
}
